/**
 * ShoppingListPage — the active shopping list view, routed by mode.
 *
 * Main app surface. Users land here after login. ShoppingListPage is a thin
 * router:
 *   - Planning mode → PlanningModeView (the expandable-availability planning
 *     surface, T12.1.5.1.2): a planning list first, additively enriched with
 *     offers.
 *   - Shopping mode → ShoppingModeView (the in-store list, T12.1.5.1.7): pending
 *     items grouped by selected store, fat single-tap completion rows, completed
 *     items hidden by default with the remaining count always visible.
 *
 * Both surfaces own their own data reads (TanStack Query) and writes (through
 * `useOrchestrators().ShoppingLists`, so the T6 reducer → storage → API contract
 * stays intact). ShoppingListPage holds the shared loading guard and the mode
 * switch.
 *
 * Architecture contract reminders:
 *   - I5: ShoppingList contains ShoppingListItems only (not products/offers
 *     directly). The API already returns items, not products.
 *   - I6: each item needs householdproductId OR selectedRetailerOfferId.
 *
 * CP5: all styling via token refs.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { useActiveShoppingList } from '@shopista/shared/api/hooks';
import { Spinner } from '../../components';
import { useAppMode } from '../../stores/useAppMode';
import { PlanningModeView } from './PlanningModeView';
import { ShoppingModeView } from './ShoppingModeView';

const loadingStyle: CSSProperties = {
  padding: 'var(--space-6)',
  textAlign: 'center',
  color: 'var(--color-text-muted)',
};

const offlineNoticeStyle: CSSProperties = {
  margin: 'var(--space-4) var(--space-4) 0',
  padding: 'var(--space-3)',
  border: 'var(--border-width-hairline) solid var(--color-border-default)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text-muted)',
};

export function ShoppingListPage(): JSX.Element {
  const listQuery = useActiveShoppingList();
  const mode = useAppMode();
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const updateConnectivity = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateConnectivity);
    window.addEventListener('offline', updateConnectivity);
    return () => {
      window.removeEventListener('online', updateConnectivity);
      window.removeEventListener('offline', updateConnectivity);
    };
  }, []);

  if (listQuery.isLoading) {
    return (
      <div data-testid="lists-loading" style={loadingStyle}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {!isOnline && (
        <p data-testid="list-offline-notice" style={offlineNoticeStyle}>
          Offline — deals and prices show when you're back online.
        </p>
      )}
      {mode === 'shopping' ? <ShoppingModeView /> : <PlanningModeView />}
    </>
  );
}
